<template>
  <div class="customer-create main-content">
    <div class="page-header">
      <h3 class="page-title">{{ $t("accounts.title") }}</h3>
      <ol class="breadcrumb">
        <li class="breadcrumb-item">
          <router-link slot="item-title" to="dashboard">
            {{ $t("general.home") }}
          </router-link>
        </li>
        <li class="breadcrumb-item">
          <router-link slot="item-title" to="#">
            {{ $tc("accounts.account", 2) }}
          </router-link>
        </li>
      </ol>
      <div class="page-actions row">
        <div class="col-xs-2 mr-4">
          <base-button
            v-show="totalAccounts || filtersApplied"
            :outline="true"
            :icon="filterIcon"
            size="large"
            color="theme"
            right-icon
            @click="toggleFilter"
          >
            {{ $t("general.filter") }}
          </base-button>
        </div>
        <router-link slot="item-title" class="col-xs-2" to="accounts/create">
          <base-button size="large" icon="plus" color="theme">
            {{ $t("accounts.new_account") }}
          </base-button>
        </router-link>
      </div>
    </div>

    <transition name="fade">
      <div v-show="showFilters" class="filter-section">
        <div class="row">
          <div class="col-sm-3">
            <label class="form-label">{{ $t("accounts.company_name") }}</label>
            <base-input
              v-model="filters.company_name"
              type="text"
              name="company_name"
              autocomplete="off"
            />
          </div>
          <div class="col-sm-3">
            <label class="form-label">{{ $t("accounts.phone") }}</label>
            <base-input
              v-model="filters.phone"
              type="text"
              name="phone"
              autocomplete="off"
            />
          </div>
          <div class="col-sm-3">
            <label class="form-label">{{ $t("accounts.status") }}</label>
            <base-input
              v-model="filters.status"
              type="text"
              name="status"
              autocomplete="off"
            />
          </div>
          <div class="col-sm-3">
            <label class="form-label">{{ $t("accounts.account_type") }}</label>
            <base-select
              v-model="filters.account_type"
              :options="this.account_types"
              :allow-empty="false"
              :show-labels="false"
              :tabindex="5"
              :placeholder="$t('accounts.select_account_type')"
              label="name"
              track-by="id"
              :custom-label="accountTypeCustomLabel"
              @select="accountType"
            />
          </div>
          <label class="clear-filter" @click="clearFilter">{{
            $t("general.clear_all")
          }}</label>
        </div>
      </div>
    </transition>

    <div
      v-cloak
      v-show="showEmptyScreen"
      class="col-xs-1 no-data-info"
      align="center"
    >
      <astronaut-icon class="mt-5 mb-4" />
      <div class="row" align="center">
        <label class="col title">{{ $t("accounts.no_accounts") }}</label>
      </div>
      <div class="row">
        <label class="description col mt-1" align="center">{{
          $t("accounts.list_of_accounts")
        }}</label>
      </div>
      <div class="btn-container">
        <base-button
          :outline="true"
          color="theme"
          class="mt-3"
          size="large"
          @click="$router.push('accounts/create')"
        >
          {{ $t("accounts.add_new_account") }}
        </base-button>
      </div>
    </div>

    <div v-show="!showEmptyScreen" class="table-container">
      <div class="table-actions mt-5">
        <p class="table-stats">
          {{ $t("general.showing") }}: <b>{{ accounts.length }}</b>
          {{ $t("general.of") }} <b>{{ totalAccounts }}</b>
        </p>

        <transition name="fade">
          <v-dropdown v-if="selectedAccounts.length" :show-arrow="false">
            <span
              slot="activator"
              href="#"
              class="table-actions-button dropdown-toggle"
            >
              {{ $t("general.actions") }}
            </span>
            <v-dropdown-item>
              <!-- <div class="dropdown-item" @click="removeMultipleAccounts">
                <font-awesome-icon :icon="['fas', 'trash']" class="dropdown-item-icon" />
                {{ $t('general.delete') }}
              </div> -->
            </v-dropdown-item>
          </v-dropdown>
        </transition>
      </div>

      <div class="custom-control custom-checkbox">
        <input
          id="select-all"
          v-model="selectAllFieldStatus"
          type="checkbox"
          class="custom-control-input"
          @change="selectAllAccounts"
        />
        <label for="select-all" class="custom-control-label selectall">
          <span class="select-all-label">{{ $t("general.select_all") }} </span>
        </label>
      </div>

      <table-component
        ref="table"
        :show-filter="false"
        :data="fetchData"
        table-class="table"
      >
        <table-column
          :sortable="false"
          :filterable="false"
          cell-class="no-click"
        >
          <template slot-scope="row">
            <div class="custom-control custom-checkbox">
              <input
                :id="row.id"
                v-model="selectField"
                :value="row.id"
                type="checkbox"
                class="custom-control-input"
              />
              <label :for="row.id" class="custom-control-label" />
            </div>
          </template>
        </table-column>

        <table-column
          :label="$t('accounts.company_name')"
          show="company_name"
        />
        <table-column :label="$t('accounts.display_name')" show="name" />
        <table-column :label="$t('accounts.phone')" show="phone" />

        <table-column
          :label="$t('accounts.added_on')"
          sort-as="created_at"
          show="formattedCreatedAt"
        />

        <table-column
          :label="$t('accounts.status')"
          sort-as="status"
          show="UpperCaseStatus"
        />
        <table-column
          :label="$t('accounts.account_type')"
          sort-as="account_type"
          show="UpperCaseAccountType"
        />
        <table-column
          :sortable="false"
          :filterable="false"
          cell-class="action-dropdown"
        >
          <template slot-scope="row">
            <span> {{ $t("accounts.action") }} </span>
            <v-dropdown>
              <a slot="activator" href="#">
                <dot-icon />
              </a>
              <v-dropdown-item v-if="row.role == 'admin'">
                <router-link
                  :to="{ path: `accounts/${row.id}/edit` }"
                  class="dropdown-item"
                >
                  <font-awesome-icon
                    :icon="['fas', 'pencil-alt']"
                    class="dropdown-item-icon"
                  />
                  {{ $t("general.edit") }}
                </router-link>
              </v-dropdown-item>
              <v-dropdown-item>
                <div class="dropdown-item" @click="viewAccountPage(row)">
                  <font-awesome-icon
                    :icon="['fas', 'eye']"
                    class="dropdown-item-icon"
                  />
                  {{ $t("general.view") }}
                </div>
              </v-dropdown-item>
              <v-dropdown-item
                v-if="
                  row.role == 'admin' &&
                    row.account_type == 'funder' &&
                    row.funder_link_audit_account === null
                "
              >
                <router-link
                  :to="{ path: `accounts/${row.id}/funder/audit` }"
                  class="dropdown-item"
                >
                  <font-awesome-icon
                    :icon="['fa', 'link']"
                    class="dropdown-item-icon"
                  />
                  {{ $t("actions.connect_to_auditor") }}
                </router-link>
              </v-dropdown-item>
              <v-dropdown-item
                v-if="
                  row.role == 'admin' &&
                    row.account_type == 'funder' &&
                    row.funder_islamic_form === null
                "
              >
                <router-link
                  :to="{ path: `accounts/${row.id}/funder/form` }"
                  class="dropdown-item"
                >
                  <font-awesome-icon
                    :icon="['fa', 'link']"
                    class="dropdown-item-icon"
                  />
                  {{ $t("actions.attach_form") }}
                </router-link>
              </v-dropdown-item>
            </v-dropdown>
          </template>
        </table-column>
      </table-component>
    </div>
  </div>
</template>
<script>
import { mapActions, mapGetters } from "vuex";
import { SweetModal, SweetModalTab } from "sweet-modal-vue";
import DotIcon from "../../components/icon/DotIcon";
import AstronautIcon from "../../components/icon/AstronautIcon";
import BaseButton from "../../../js/components/base/BaseButton";
import { request } from "http";

export default {
  components: {
    DotIcon,
    AstronautIcon,
    SweetModal,
    SweetModalTab,
    BaseButton
  },
  data() {
    return {
      userAccounts: [],
      showFilters: false,
      filtersApplied: false,
      isRequestOngoing: true,
      account_types: [],

      filters: {
        display_name: "",
        company_name: "",
        phone: "",
        role: "",
        status: "",
        account_type: ""
      }
    };
  },
  computed: {
    showEmptyScreen() {
      return (
        !this.totalAccounts && !this.isRequestOngoing && !this.filtersApplied
      );
    },
    filterIcon() {
      return this.showFilters ? "times" : "filter";
    },
    ...mapGetters("account", [
      "accounts",
      "selectedAccounts",
      "totalAccounts",
      "selectAllField"
    ]),
    selectField: {
      get: function() {
        return this.selectedAccounts;
      },
      set: function(val) {
        this.selectAccount(val);
      }
    },
    selectAllFieldStatus: {
      get: function() {
        return this.selectAllField;
      },
      set: function(val) {
        this.setSelectAllState(val);
      }
    }
  },
  watch: {
    filters: {
      handler: "setFilters",
      deep: true
    }
  },
  destroyed() {
    if (this.selectAllField) {
      this.selectAllAccounts();
    }
  },
  methods: {
    accountType(val) {
      this.filters.account_type = val.name.toLowerCase().replace(" ", "_");
    },
    accountTypeCustomLabel({ name }) {
      return name.toUpperCase().replace("_", " ");
    },
    ...mapActions("account", [
      "fetchAccounts",
      "selectAllAccounts",
      "selectAccount",
      "deleteAccount",
      "deleteMultipleAccounts",
      "setSelectAllState",
      "viewAccount"
    ]),
    refreshTable() {
      this.$refs.table.refresh();
    },
    async fetchData({ page, per_page, filter, sort }) {
      let data = {
        display_name: this.filters.display_name,
        company_name: this.filters.company_name,
        phone: this.filters.phone,
        role: this.filters.role,
        status: this.filters.status,
        account_type: this.filters.account_type.name,
        orderByField: sort.fieldName || "users.created_at",
        orderBy: sort.order || "desc",
        per_page, // Add rowsPerPage in the API request
        page
      };
      this.isRequestOngoing = true;

      let response = await this.fetchAccounts(data);

      this.isRequestOngoing = false;
      this.account_types = response.data.account_types;

      this.userAccounts = response.data.accounts.data;

      return {
        data: this.userAccounts,
        pagination: {
          totalPages: response.data.accounts.last_page,
          currentPage: page,
          rowsPerPage: response.data.accounts.per_page // Add rowsPerPage in the API request
        }
      };
    },
    setFilters() {
      this.filtersApplied = true;
      this.refreshTable();
    },
    clearFilter() {
      this.filters = {
        display_name: "",
        company_name: "",
        phone: "",
        role: "",
        status: "",
        account_type: ""
      };

      this.$nextTick(() => {
        this.filtersApplied = false;
      });
    },
    toggleFilter() {
      if (this.showFilters && this.filtersApplied) {
        this.clearFilter();
        this.refreshTable();
      }

      this.showFilters = !this.showFilters;
    },
    async removeAccount(id) {
      swal({
        title: this.$t("general.are_you_sure"),
        text: this.$tc("accounts.confirm_delete"),
        icon: "/assets/icon/trash-solid.svg",
        buttons: true,
        dangerMode: true
      }).then(async willDelete => {
        if (willDelete) {
          let res = await this.deleteAccount(id);
          if (res.data.success) {
            window.toastr["success"](this.$tc("accounts.deleted_message"));
            this.refreshTable();
            return true;
          } else if (request.data.error) {
            window.toastr["error"](res.data.message);
          }
        }
      });
    },
    async removeMultipleAccounts() {
      swal({
        title: this.$t("general.are_you_sure"),
        text: this.$tc("accounts.confirm_delete", 2),
        icon: "/assets/icon/trash-solid.svg",
        buttons: true,
        dangerMode: true
      }).then(async willDelete => {
        if (willDelete) {
          let request = await this.deleteMultipleAccounts();
          if (request.data.success) {
            window.toastr["success"](this.$tc("accounts.deleted_message", 2));
            this.refreshTable();
          } else if (request.data.error) {
            window.toastr["error"](request.data.message);
          }
        }
      });
    },
    async viewAccountPage(row) {
      this.$router.push(`/super-admin/accounts/${row.id}/view`);
      let res = await this.viewAccount(row);
    }
  }
};
</script>
